/**
 * Generic single-order detail inserter for Postgres-backed tables.
 * @module singleOrderDetailsGenericInsert
 */

import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { getBearerToken } from "../../postCall/bearerToken";

/** Parameters for inserting a single order's details into a specific Postgres table. */
export interface SingleOrderDetailsGenericInsertParams<T> {
  order_id: string;
  org_id: string;
  /** Target table/resource to insert into (e.g. coverage, order, clinical-data). */
  table_name: SingleOrderDetailsTableName;
  /** Data payload to insert into the target table. */
  data: T;
}

/** Response shape returned by the Postgres insert endpoint. */
export interface SingleOrderDetailsGenericInsertResponse {
  success: boolean;
  inserted_rows: number;
}

/**
 * Inserts details for a single medical PA order into a specified Postgres table.
 *
 * @typeParam T - Shape of the insert payload sent in the request body.
 * @param params - Order ID, org ID, target table name, and the data to insert.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns The insert result indicating success and number of inserted rows.
 * @throws Re-throws any network or parsing error after logging.
 */
export const singleOrderDetailsGenericInsert = async <T>(
  params: SingleOrderDetailsGenericInsertParams<T>,
  signal?: AbortSignal,
): Promise<SingleOrderDetailsGenericInsertResponse> => {
  try {
    const url = `${API_ENDPOINTS.SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES_INSERT}/${params.table_name}`;
    const bearerToken = await getBearerToken();
    const raw = JSON.stringify({
      order_id: params.order_id,
      org_id: params.org_id,
      data: params.data,
    });
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: raw,
      ...(signal && { signal }),
    });
    const result: SingleOrderDetailsGenericInsertResponse =
      await response.json();
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
